import {
  INSURANCE_PAYER_STATE_TAG_SYSTEM,
  INSURER_ORGANIZATION_TYPE_CODE,
  ORGANIZATION_TYPE_CODESYSTEM,
} from '@bariendo/lib/insurance-coverage';
import { payerKey } from '@bariendo/lib/insurance-prefill';
import { getReferenceString, MedplumClient } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';

export interface InsurerOption {
  name: string;
  reference: string;
  key: string;
}

export interface InsurerOptions {
  options: InsurerOption[];
  loading: boolean;
  failed: boolean;
}

/**
 * Medplum's active insurer Organizations with a payer key, by name, one entry per name: the
 * list bariendo-be seeds and signup offers. Scoped to `state` when one is given.
 */
export async function loadInsurerOptions(medplum: MedplumClient, state?: string | null): Promise<InsurerOption[]> {
  const organizations = await medplum.searchResources('Organization', {
    type: `${ORGANIZATION_TYPE_CODESYSTEM}|${INSURER_ORGANIZATION_TYPE_CODE}`,
    ...(state ? { _tag: `${INSURANCE_PAYER_STATE_TAG_SYSTEM}|${state}` } : {}),
    active: 'true',
    _sort: 'name',
    _count: 1000,
  });
  return organizations
    .flatMap((organization) => {
      const reference = getReferenceString(organization);
      const key = payerKey(organization);
      return reference && organization.name && key ? [{ name: organization.name, reference, key }] : [];
    })
    .filter((option, index, all) => all.findIndex((other) => other.name === option.name) === index);
}

/**
 * The insurers for the patient's state (BAR-2919): Medplum's insurer Organizations tagged with
 * the state, the list bariendo-be seeds and signup offers. Every state's when the state isn't
 * known. Only insurers with a payer key, as bariendo-be offers at signup: the key is what the
 * criteria (Cigna's bars) and network read. One entry per name, since the selects are keyed by it.
 *
 * Not read while `enabled` is false, or while the state is still being looked up (`stateLoading`),
 * so the list isn't read twice.
 */
export function useInsurerOptions(
  state: string | null | undefined,
  { enabled, stateLoading }: { enabled: boolean; stateLoading?: boolean }
): InsurerOptions {
  const medplum = useMedplum();
  const [options, setOptions] = useState<InsurerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled || stateLoading) return undefined;
    let cancelled = false;
    // Not the previous state's (or patient's) list while this one loads.
    setOptions([]);
    setLoading(true);
    setFailed(false);
    const load = async (): Promise<void> => {
      try {
        const loaded = await loadInsurerOptions(medplum, state);
        if (cancelled) return;
        setOptions(loaded);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [medplum, state, stateLoading, enabled]);

  return { options, loading, failed };
}
